import type { UserRole } from "@src/context/ApplicationContext";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useFeatureFlags } from "@src/context/FeatureFlagContext";
import { calculateAgeInYears, isValidDateOfBirth } from "@src/utils/date-utils";
import { isUnder18AgeRange } from "@src/utils/scheduling-metadata";

export type LabelResolutionInputs = {
  role: UserRole;
  enabled: boolean;
  age: number | undefined;
};

/**
 * Gathers the shared inputs used to resolve label voice/labels from context:
 * user role, the FEATURE_LABEL flag, and the effective age (explicit option →
 * DOB-derived → ageRange-derived sentinel). Age only applies to the patient
 * (self-booking) role; navigator/referrer flows stay role-based.
 *
 * Consumed by both `useLabelMode` (voice) and `useResolvedPageLabels` (labels)
 * so the DOB/age-range logic lives in one place.
 *
 * @param options.age - Optional explicit age; takes priority over the derived DOB age.
 */
export function useLabelResolutionInputs(options?: { age?: number }): LabelResolutionInputs {
  const { data } = useApplicationContext();
  const { FEATURE_LABEL } = useFeatureFlags();
  const role = data.userRole ?? "patient";

  const dobAge =
    data.dob && isValidDateOfBirth(data.dob) ? calculateAgeInYears(data.dob) : undefined;

  // Fall back to age range when DOB is not available yet.
  // 0 => under 18 (minor voice), 18 => adult voice.
  let ageRangeAge: number | undefined;
  if (typeof data.ageRange === "string" && data.ageRange.trim() !== "") {
    ageRangeAge = isUnder18AgeRange(data.ageRange) ? 0 : 18;
  }

  // Age-based resolution is limited to the patient (self-booking) role.
  const age = role === "patient" ? (options?.age ?? dobAge ?? ageRangeAge) : undefined;

  return { role, enabled: Boolean(FEATURE_LABEL), age };
}
